import React, { useEffect, useState } from 'react';
import { M } from '@dashboard-experience/mastodon';
import { Translate as T } from 'react-redux-i18n';
import { connect } from 'react-redux';
import { useFlagr } from 'api/flagr';

import { PERSONAL_CHECKS_ANALYTICS_EVENTS } from 'lib/analytics';
import { trackAnalyticsEvent } from 'actions/analytics';
import styles from './PersonalChecksBanner.module.scss';
import { FLAGR_KEYS } from '../../constants';

const BANNER_DISMISSED_KEY = 'personalChecksBannerDismissed';
const BANNER_COPY_VERSION = '08-07-2025';

interface Report {
  accountId: string;
  candidateId: string;
  [key: string]: any;
}

const hasBlockedAccountReports = (
  reports: Report[],
  blockedAccountIds: string[],
) => {
  if (!reports || !blockedAccountIds) return true;
  const blockedAccountIdsSet = new Set(blockedAccountIds);
  return reports.some((report: any) => {
    return blockedAccountIdsSet.has(report.accountId);
  });
};

const hasIncompleteReports = (reports?: Report[]) => {
  if (!reports) return true;
  return reports.some((report: any) => {
    return (
      report?.displayStatus?.toLowerCase() !== 'complete' ||
      report?.status?.toLowerCase() !== 'clear'
    );
  });
};

const generatePersonalChecksUrl = (candidateId: string | undefined): string => {
  const baseUrl = 'https://checkr.com/profiles';

  const params = new URLSearchParams({
    utm_source: 'candidate_portal',
    utm_medium: 'personal_checks_banner',
    utm_campaign: 'personal_checks_banner',
    utm_content: candidateId || 'no_candidate_id',
  });

  return `${baseUrl}?${params.toString()}`;
};

const useBannerDismissedState = () => {
  const [isDismissed, setIsDismissed] = useState(
    () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true',
  );

  const setAndPersistIsDismissed = (dismissed: boolean) => {
    setIsDismissed(dismissed);
    localStorage.setItem(BANNER_DISMISSED_KEY, String(dismissed));
  };

  return [isDismissed, setAndPersistIsDismissed] as const;
};

// Container to control the display logic of the banner
function PersonalChecksBannerContainer({
  reports,
  applicantId,
  trackEvent,
}: {
  reports: Report[];
  applicantId: string;
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;
}) {
  const [isDismissed, setIsDismissed] = useBannerDismissedState();

  const { flag, isFetched: flagIsLoaded } = useFlagr({
    context: {
      locationHost: window.location.host,
      applicantId,
    },
    id: FLAGR_KEYS.personalChecksBannerFlagKey,
    entity: { id: applicantId, type: 'applicantId' },
    fallbackVariant: 'off',
  });

  if (isDismissed || !flagIsLoaded || flag.variantKey !== 'on') {
    return null;
  }

  const blockedAccountIds: string[] = flag.variantAttachment?.blockedAccountIds;

  // Don't show banner if the applicant has blocked accounts
  if (hasBlockedAccountReports(reports, blockedAccountIds)) {
    return null;
  }

  // Don't show banner if any report is incomplete
  if (hasIncompleteReports(reports)) {
    return null;
  }

  return (
    <PersonalChecksBanner
      onDismiss={setIsDismissed}
      trackEvent={trackEvent}
      applicantId={applicantId}
      reports={reports}
    />
  );
}

const mapStateToProps = (state: any) => {
  const { reports, applicantId } = state.portal;

  return { reports, applicantId };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    trackEvent: (eventName: string, eventParams?: Record<string, any>) =>
      dispatch(trackAnalyticsEvent(eventName, eventParams)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PersonalChecksBannerContainer);

function PersonalChecksBanner({
  onDismiss,
  trackEvent,
  applicantId,
  reports,
}: {
  onDismiss: (dismissed: boolean) => void;
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;
  applicantId: string;
  reports: Report[];
}) {
  useEffect(() => {
    trackEvent(PERSONAL_CHECKS_ANALYTICS_EVENTS.PERSONAL_CHECKS_BANNER_VIEWED, {
      applicantId,
      reportIds: reports.map((report: any) => report.id),
      copyVersion: BANNER_COPY_VERSION,
    });
  }, [applicantId, reports, trackEvent]);

  const handleDismissClick = () => {
    trackEvent(
      PERSONAL_CHECKS_ANALYTICS_EVENTS.PERSONAL_CHECKS_DISMISS_CLICKED,
      {
        applicantId,
        copyVersion: BANNER_COPY_VERSION,
      },
    );
    onDismiss(true);
  };

  const handleCtaClick = () => {
    trackEvent(
      PERSONAL_CHECKS_ANALYTICS_EVENTS.PERSONAL_CHECKS_BANNER_CTA_CLICKED,
      {
        applicantId,
        copyVersion: BANNER_COPY_VERSION,
      },
    );
  };

  const candidateId = reports[0]?.candidateId;

  return (
    <div className={styles.personalChecksBanner}>
      <button
        className={styles.dismissButton}
        onClick={handleDismissClick}
        type='button'
        aria-label='Dismiss personal checks banner'
      >
        <M.Icon
          icon='Close'
          style={{ fill: 'rgba(107, 114, 128)' }}
          size={24}
        />
      </button>
      <div className={styles.bannerContent}>
        <h4 className={styles.header}>
          <T value='components.PersonalChecks.banner.header' />
        </h4>
        <p className={styles.subheader}>
          <T value='components.PersonalChecks.banner.subheader' />
        </p>
        <a
          href={generatePersonalChecksUrl(candidateId)}
          target='_blank'
          rel='noopener noreferrer'
          className={styles.claimButton}
          onClick={handleCtaClick}
        >
          <T value='components.PersonalChecks.banner.buttonText' />
          <svg
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M11.25 3.75012L10.3562 4.62075L15.0938 9.37512H2.5V10.6251H15.0938L10.3562 15.3582L11.25 16.2501L17.5 10.0001L11.25 3.75012Z'
              fill='white'
            />
          </svg>
        </a>

        <p className={styles.trustText}>
          <T value='components.PersonalChecks.banner.trust' dangerousHTML />
        </p>
      </div>
    </div>
  );
}
